/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
import { useTranslation } from "@i18n";
import { useCallback, useEffect, useMemo, useState, useRef, type ComponentType } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import IconComponent from "@CubeComponents/icons";
import DeleteIcon from "@Components/StickySidebarViewer/assets/DeleteIcon";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import { getBackendUrl } from "@Utils";
import SlickGrid1 from "@CubeComponents/html/SlickGrid1";
import { PlusIcon } from "@Components/StickySidebarViewer/assets/selectionIcons";
import { renderToStaticMarkup } from "react-dom/server";
import { HeaderWithIcon } from "@CubeComponents/html/SlickGrid1/Formatters/Formatters";
import { updateGroupsData } from "@Actions/portalSetting";
import { getPortalProfiles } from "@Actions/portalSetting.helper";
import AutoComplete from "@CubeComponents/Mui_Form/AutoComplete";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import { portalCreateRoot as createRoot } from "@CubeComponents/html/cellPortals";
import { AddUsersToProject } from "@Features/projects";
import { showToastNotification } from "@Reducers/toastSlice";
import CheckBoxComponent from "@CubeComponents/Mui_Form/CheckBox";
import RolesModalContent from "./RolesModalContent";
import BulkUpdateRolesModal from "./BulkUpdateRolesModal";
import { RootState } from "@Reducers/index";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";
import type { PortalProfileItem } from "@ServerTypes/portal/portal.schema";
import type { UpdateGroupsDataRequest } from "@ServerTypes/groups/groups.schema";
import type { GridRowBase, SlickGrid1Ref, ValidatedColumnDefinition } from "@CubeComponents/html/SlickGrid1";

/** A member row of the `groups/getGroups` grid (the group's `users[]` projection). */
interface GroupMemberRow extends GridRowBase {
  _id?: ObjectIdString;
  id?: ObjectIdString;
  userId?: ObjectIdString;
  name?: string;
  portalRole?: ObjectIdString;
  portalRoleName?: string;
  designation?: string;
  organisation?: string;
  createdDate?: number;
  [key: string]: unknown;
}

/**
 * Payload for `/groups/removeUsersFromGroups`. `data` is an ARRAY of user ids here, not the object
 * `updateGroupsDataRequest.data` declares — and `removeFromResources` is not declared on that schema
 * at all. See the KNOWN BUG note on `handleCheckboxChange`.
 */
type RemoveUsersPayload = {
  data: Array<ObjectIdString | undefined>;
  action: "remove_user";
  groupId?: ObjectIdString;
  removeFromResources: boolean;
};

/** The `{ data, action, groupId }` envelope every write on this screen posts through updateGroupsData. */
type GroupsWritePayload = { data: unknown; action: "insert" | "update"; groupId?: ObjectIdString };

/** `onSelectedRowIdsChanged` detail as this grid reads it (SlickGrid1 types the arg as a loose record). */
type SelectedRowIdsChangedArgs = { detail?: { args?: { selectedRowIds?: Array<string | number> } } };

function Members() {
  const { t } = useTranslation();
  const [openAddUserModal, setOpenAddUserModal] = useState(false);
  const [openBulkUpdateModal, setOpenBulkUpdateModal] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<GroupMemberRow[]>([]);
  const [bulkSelectedRole, setBulkSelectedRole] = useState<GroupsWritePayload | Record<string, never>>({});
  const [openRemoveUserModal, setOpenRemoveUserModal] = useState(false);
  const [nextEnabled, setNextEnabled] = useState(false);
  const [removeUsersPayload, setRemoveUsersPayload] = useState<Partial<RemoveUsersPayload>>({});
  const [toolbarButtonState, setToolbarButtonState] = useState({
    remove: true,
    bulkUpdate: true,
  });
  const dispatch = useDispatch();
  // Starts as `{}` (not `[]`): the grid — and therefore the cell formatter that reads this — only mounts
  // once `roleData` is non-empty, by which time the sync effect below has replaced it with the array.
  const roleDataRef = useRef<PortalProfileItem[] | Record<string, never>>({});
  const AutoCompleteAny = AutoComplete;

  const { portalSettingInfo } = useSelector((state: RootState) => state);
  const canEditGroups = !!portalSettingInfo?.groups?.editGroups?.enable;

  const roleData = useSelector((state: RootState) => state.profileData);
  const dataGrid = useRef<SlickGrid1Ref<GroupMemberRow> | null>(null);
  const userDataGrid = useRef<SlickGrid1Ref<GroupMemberRow> | null>(null);
  // Route params enter from outside the type system; the backend route schema validates these ids.
  const _routeParams = useParams<{ groupId: ObjectIdString }>();
  const paraGrpId = useParams<{ costDatabaseId: ObjectIdString }>();
  const params = { groupId: paraGrpId.costDatabaseId || _routeParams.groupId };

  const remoteDataSource = `${getBackendUrl()}/groups/getGroups`;
  const query = useMemo(() => (params.groupId ? { params: { groupId: params.groupId } } : {}), [params.groupId]);

  useEffect(() => {
    roleDataRef.current = roleData;
  }, [roleData]);

  const onSelectedRowIdsChanged = (args: Record<string, unknown>) => {
    const selectedIds = (args as SelectedRowIdsChangedArgs)?.detail?.args?.selectedRowIds || [];
    const totalRowsSelected = selectedIds.length;

    if (totalRowsSelected === 0) {
      setToolbarButtonState({ remove: true, bulkUpdate: true });
    } else {
      setToolbarButtonState({ remove: false, bulkUpdate: false });
    }
  };

  const toolbarClickHandler = useCallback(
    async (toolClicked: string) => {
      if (toolClicked === "add_user") {
        setOpenAddUserModal(true);
      }
      if (toolClicked === "remove_user") {
        const selectedData = userDataGrid.current?.dataView?.getAllSelectedItems<GroupMemberRow>() || [];
        const userIds = selectedData.map((user) => user.userId);
        setOpenRemoveUserModal(true);
        setRemoveUsersPayload({
          data: userIds,
          action: "remove_user",
          groupId: params.groupId,
          removeFromResources: false,
        });
      }
      if (toolClicked === "bulk_update") {
        setOpenBulkUpdateModal(true);
        setSelectedRowData(userDataGrid.current?.dataView?.getAllSelectedItems() || []);
      }
    },
    [params.groupId],
  );

  const handleCancelAddUsersModal = () => {
    setNextEnabled(false);
    setOpenAddUserModal(false);
    setOpenBulkUpdateModal(false);
    dataGrid.current?.refresh!();
    userDataGrid.current?.refresh!();
  };

  const handleAddClick = async (selectedData: unknown) => {
    const groupId = params.groupId;
    const data = {
      data: selectedData,
      action: "insert" as const,
      groupId,
    };
    const response = await updateGroupsData(data as UpdateGroupsDataRequest);
    if (response.data.status) {
      dispatch(
        showToastNotification({
          content: t("The selected user(s) have been added successfully."),
        }),
      );
    }
    handleCancelAddUsersModal();
    userDataGrid.current?.refresh!();
  };

  const setRoleForAllUsers = async () => {
    const response = await updateGroupsData(bulkSelectedRole as UpdateGroupsDataRequest);
    if (response.data.status) {
      dispatch(
        showToastNotification({
          content: t("The roles of selected user(s) have been updated successfully."),
        }),
      );
    }
    handleCancelAddUsersModal();
  };

  const handleBulkRuleChange = (e: { value?: ObjectIdString; itemData?: { frontendName?: string } | null }) => {
    const userIds = selectedRowData.map((data) => data.userId);
    const roleDataPayload = {
      roleChangedTo: e.itemData?.frontendName,
      roleId: e.value,
      userId: userIds,
    };
    const groupId = params.groupId;
    const data: GroupsWritePayload = {
      data: roleDataPayload,
      action: "update" as const,
      groupId,
    };
    setBulkSelectedRole(data);
    setNextEnabled(true);
  };

  const AssignRolesModalContent = () =>
    RolesModalContent({
      value: bulkSelectedRole,
      dataSource: roleData,
      onChange: handleBulkRuleChange,
    });

  const handleAddEditModalCLose = () => {
    setOpenRemoveUserModal(false);
    userDataGrid.current?.refresh!();
  };

  // KNOWN BUG, preserved verbatim: `removeFromResources` is NOT declared on `updateGroupsDataRequest`,
  // the schema that validates POST /groups/removeUsersFromGroups, so Zod strips it before
  // `removeUsersFromGroups` reads it — this checkbox has no effect. The fix would be to declare the
  // field on that request schema (server-side, out of scope here).
  const handleCheckboxChange = (args: { checked: boolean }) => {
    setRemoveUsersPayload((prev) => ({
      ...prev,
      removeFromResources: args.checked,
    }));
  };

  const removeUsers = () => (
    <ModalStateDialogComponent
      allowModalPadding={false}
      footerContent={
        <div className="flex justify-normal gap-4 pb-4 ps-4">
          <ButtonComponent buttonType="gradientButton" onClick={removeUsersFromGroup}>
            {t("Confirm")}
          </ButtonComponent>
          <ButtonComponent
            buttonType="borderButton"
            onClick={() => {
              setOpenRemoveUserModal(false);
            }}
          >
            {t("Cancel")}
          </ButtonComponent>
        </div>
      }
      width="30%"
      headerTitle={
        <div className="flex justify-between w-full">
          <span>{t("Remove User(s)")}</span>
        </div>
      }
      onCancel={handleAddEditModalCLose}
      isVisible={openRemoveUserModal}
      modelContent={
        <div className="flex flex-col justify-center ms-4 pb-4 me-1 ">
          <div className="mb-4">{t("Are you sure you want to remove the selected user(s) from the group?")}</div>
          <div className="font-bold">
            {t(
              "Note:- Removing this user from the group will result in the removal of that user from all the related entities.",
            )}
          </div>
          <span>
            {" "}
            <CheckBoxComponent
              label="Also, remove the user from the projects/ spaces they're part of"
              id="deleteTask"
              change={handleCheckboxChange}
            />{" "}
          </span>
        </div>
      }
    />
  );

  const updateRolesBulk = () =>
    BulkUpdateRolesModal({
      headerText: t("Update Role(s)"),
      closeModal: handleCancelAddUsersModal,
      modalState: true,
      confirmClick: setRoleForAllUsers,
      confirmEnabled: nextEnabled,
      modalContent: AssignRolesModalContent,
    });

  useEffect(() => {
    dispatch(getPortalProfiles());
  }, []);

  const addButtonTemplate = useCallback(
    () => (
      <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON} id="add_user" disabled={!canEditGroups}>
        <div className="flex justify-center items-center gap-2">
          <PlusIcon color={"var(--primary-text)"} />
          <span>{t("Add")}</span>
        </div>
      </ButtonComponent>
    ),
    [canEditGroups],
  );

  const deleteBtnTemplate = useCallback(
    () => (
      <div>
        <ButtonComponent buttonType={BUTTON_TYPES.TEXT_BUTTON} id="remove_user" disabled={toolbarButtonState.remove}>
          <span className="flex flex-row items-center justify-start ">
            <DeleteIcon size="15" className="me-2" />
            {t("Remove")}
          </span>
        </ButtonComponent>
      </div>
    ),
    [toolbarButtonState.remove],
  );

  const bulkUpdtTemplate = useCallback(
    () => (
      <div>
        <ButtonComponent
          buttonType={BUTTON_TYPES.TEXT_BUTTON}
          id="bulk_update"
          disabled={toolbarButtonState.bulkUpdate}
        >
          <span className="flex flex-row items-center justify-start ">
            <IconComponent name="Update" width="1.5rem" height="1.5rem" className="me-2" />
            {t("Bulk Update Roles")}
          </span>
        </ButtonComponent>
      </div>
    ),
    [toolbarButtonState.bulkUpdate],
  );

  const toolbarOptions = useMemo(
    () => [
      { id: "add_user", template: addButtonTemplate },
      { id: "remove_user", template: deleteBtnTemplate, disabled: toolbarButtonState.remove },
      { id: "bulk_update", template: bulkUpdtTemplate, disabled: toolbarButtonState.bulkUpdate },
    ],
    [toolbarButtonState.remove, toolbarButtonState.bulkUpdate, addButtonTemplate, deleteBtnTemplate, bulkUpdtTemplate],
  );

  const removeUsersFromGroup = async () => {
    setOpenRemoveUserModal(false);
    const res = await updateGroupsData(removeUsersPayload as UpdateGroupsDataRequest);
    if (res) {
      userDataGrid.current?.refresh!();
    }
  };

  async function handleChange(e: { target: { typedString?: string; value?: ObjectIdString } }, props: GroupMemberRow) {
    const roleDataPayload = {
      roleChangedTo: e.target.typedString,
      roleId: e.target.value,
      userId: props.userId,
      selectedUserfromRow: props.name,
    };
    const groupId = params.groupId;
    const data: GroupsWritePayload = {
      data: roleDataPayload,
      action: "update" as const,
      groupId,
    };
    await updateGroupsData(data as UpdateGroupsDataRequest);
    handleCancelAddUsersModal();
  }

  const portalRoleFormatter = (
    _row: number,
    _cell: number,
    _value: unknown,
    colDef: unknown,
    dataContext: GroupMemberRow,
  ) => {
    const container = document.createElement("div");
    container.style.width = "100%";
    const options = (roleDataRef.current || []) as PortalProfileItem[];
    const root = createRoot(container);
    root.render(
      <AutoCompleteAny
        value={options.find((opt) => opt.id === dataContext.portalRole) || null}
        options={options}
        valueField="id"
        getOptionLabel={(option) => option?.frontendName || ""}
        onChange={(_event, newValue) =>
          handleChange({ target: { typedString: newValue?.frontendName, value: newValue?.id } }, dataContext)
        }
        disabled={!canEditGroups}
        renderInput={(params) => <TextFieldComponent {...params} />}
      />,
    );
    return container;
  };

  const openAddUsersModal = () => {
    setOpenAddUserModal(!openAddUserModal);
  };

  const nameHeaderRenderer = () => renderToStaticMarkup(<HeaderWithIcon icon="Textbox" label={t("Name")} />);
  const portalRoleHeaderRenderer = () =>
    renderToStaticMarkup(<HeaderWithIcon icon="Dropdown" label={t("Portal Role")} />);
  const designationHeaderRenderer = () =>
    renderToStaticMarkup(<HeaderWithIcon icon="Textbox" label={t("Designation")} />);
  const organisationHeaderRenderer = () =>
    renderToStaticMarkup(<HeaderWithIcon icon="Textbox" label={t("Organisation")} />);
  const addedOnHeaderRenderer = () =>
    renderToStaticMarkup(<HeaderWithIcon icon="Date and Time" label={t("Added on")} />);

  const columnDefinitions = useMemo(
    () => [
      {
        id: "_id",
        field: "_id",
        name: "_id",
        hidden: true,
      },
      {
        id: "name",
        field: "name",
        name: t("Name"),
        width: 180,
        originalWidth: 180,
        filterType: "Text",
        clipMode: "EllipsisWithTooltip",
        headerRenderer: nameHeaderRenderer,
      },
      {
        id: "portalRoleName",
        field: "portalRoleName",
        name: t("Portal Role"),
        width: 220,
        originalWidth: 220,
        formatter: portalRoleFormatter,
        disableSorting: true,
        disableFiltering: true,
        disableGrouping: true,
        headerRenderer: portalRoleHeaderRenderer,
      },
      {
        id: "designation",
        field: "designation",
        name: t("Designation"),
        width: 180,
        originalWidth: 180,
        filterType: "Text",
        clipMode: "EllipsisWithTooltip",
        headerRenderer: designationHeaderRenderer,
      },
      {
        id: "organisation",
        field: "organisation",
        name: t("Organisation"),
        width: 180,
        originalWidth: 180,
        filterType: "Text",
        clipMode: "EllipsisWithTooltip",
        headerRenderer: organisationHeaderRenderer,
      },
      {
        id: "createdDate",
        field: "createdDate",
        name: t("Added on"),
        width: 180,
        originalWidth: 180,
        formatterType: "Date",
        filterType: "Date",
        clipMode: "EllipsisWithTooltip",
        headerRenderer: addedOnHeaderRenderer,
      },
    ],

    [canEditGroups],
  );

  const groupMembers = () => (
    <div className="overflow-auto h-full">
      <SlickGrid1
        ref={userDataGrid}
        columnDefinitions={columnDefinitions as unknown as ValidatedColumnDefinition<GroupMemberRow>[]}
        remoteDataSource={remoteDataSource}
        query={query}
        toolbar={toolbarOptions}
        toolbarClickHandler={toolbarClickHandler}
        onSelectedRowIdsChanged={onSelectedRowIdsChanged}
        enableFilteringOption
        enableSortingOption
        enableGroupingOption
        enableColumnChooser
        enableRowResize
        rowHeight={68}
        excelExport
        hideNoDataIllustration
      />
    </div>
  );

  // `AddUsersToProject` declares projectId/selectUser/refreshAlreadyAddedUserData/excludedUsers as
  // REQUIRED (all `any`); this screen supplies only the group-flow subset, so narrow the component type
  // to the props actually passed. Widening those props belongs to features/projects, not here.
  const AddUsersAny = AddUsersToProject as unknown as ComponentType<{
    source: "portalSetting" | "projectSetting";
    closeModal: () => void;
    customOnNext: (selectedData: unknown) => void;
    groupId?: ObjectIdString;
    customHeader?: string;
  }>;

  return (
    <div className="w-full flex flex-col h-full overflow-auto">
      {roleData && roleData.length && groupMembers()}
      {openAddUserModal && (
        <AddUsersAny
          source={"portalSetting"}
          closeModal={openAddUsersModal}
          customOnNext={handleAddClick}
          groupId={params.groupId}
          customHeader="Add Users to Group"
        />
      )}
      {openBulkUpdateModal && updateRolesBulk()}
      {openRemoveUserModal && removeUsers()}
    </div>
  );
}
Members.displayName = "Members";
export default Members;
